import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { JobCard } from "@/components/JobCard";
import { Search, Loader2, ShieldCheck, GraduationCap, BookOpenCheck, MessageSquareText, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1710473492229-888b362df2af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjYW1waW5hcyUyMGNpdHklMjBicmF6aWx8ZW58MHx8fHwxNzg2NzA2MDcxfDA&ixlib=rb-4.1.0&q=85";

const TOOLS = [
  {
    to: "/vaga-limpa",
    title: "VagaLimpa",
    text: "Veja quais anúncios trazem mais informações antes de se candidatar.",
    icon: ShieldCheck,
    className: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  {
    to: "/primeiro-emprego",
    title: "Primeiro Emprego",
    text: "Encontre oportunidades que podem combinar com quem está começando.",
    icon: GraduationCap,
    className: "bg-blue-50 text-blue-800 border-blue-200",
  },
  {
    to: "/estagios",
    title: "Estágio Limpo",
    text: "Separe estágio, jovem aprendiz e aprendizagem das demais vagas.",
    icon: BookOpenCheck,
    className: "bg-teal-50 text-teal-800 border-teal-200",
  },
  {
    to: "/entrevista-treino",
    title: "Entrevista Treino",
    text: "Pratique respostas no navegador, sem IA paga e sem cadastro extra.",
    icon: MessageSquareText,
    className: "bg-violet-50 text-violet-800 border-violet-200",
  },
];

export default function Home() {
  const [vagas, setVagas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [cidade, setCidade] = useState("");
  const [cidades, setCidades] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/vagas", { params: { q: busca || undefined, cidade: cidade || undefined } });
      setVagas(res.data);
    } finally {
      setLoading(false);
    }
  }, [busca, cidade]);

  useEffect(() => {
    api.get("/meta/cidades").then((r) => setCidades(r.data));
  }, []);
  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const limpar = () => {
    setBusca("");
    setCidade("");
    setTimeout(() => api.get("/vagas").then((r) => setVagas(r.data)), 0);
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-700 to-indigo-900 text-white grain">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center mix-blend-overlay"
          style={{ backgroundImage: `url(${HERO})` }}
        />
        <div className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-block bg-white/15 border border-white/20 rounded-full px-4 py-1 text-sm mb-5">
              Campinas • Hortolândia • Sumaré • Valinhos • Vinhedo • Paulínia
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Encontre vagas e se prepare melhor para conseguir a oportunidade
            </h1>
            <p className="mt-5 text-lg text-blue-100 leading-relaxed max-w-lg">
              Vagas da Região Metropolitana de Campinas com ferramentas gratuitas para conferir anúncios, encontrar primeiro emprego, estágio e treinar entrevistas.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur border border-white/20 rounded-3xl p-6 shadow-2xl">
            <h3 className="font-heading font-extrabold text-xl mb-4">Buscar vaga</h3>
            <div className="grid gap-3">
              <input
                data-testid="search-input"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                type="text"
                placeholder="Cargo, empresa ou palavra-chave"
                className="w-full rounded-xl px-4 py-3 text-slate-900 outline-none"
              />
              <select
                data-testid="search-city"
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-slate-900 outline-none"
              >
                <option value="">Todas as cidades</option>
                {cidades.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button
                data-testid="search-button"
                onClick={load}
                className="bg-emerald-400 text-slate-950 font-extrabold rounded-xl py-3 hover:bg-emerald-500 hover:-translate-y-1 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Search size={18} /> Pesquisar
              </button>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-5 py-12">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            ["RMC", "Campinas e região"],
            ["Grátis", "para candidatos"],
            ["4 ferramentas", "para se preparar"],
            ["Diário", "vagas atualizadas"],
          ].map(([a, b]) => (
            <div key={a} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <strong className="text-2xl font-heading tracking-tight">{a}</strong>
              <p className="text-slate-600 mt-1">{b}</p>
            </div>
          ))}
        </section>

        <section className="mb-14">
          <div className="mb-6">
            <span className="text-sm uppercase tracking-wider font-extrabold text-blue-700">Para candidatos</span>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-950 mt-1">Não é só achar a vaga. É chegar mais preparado.</h2>
            <p className="text-slate-600 mt-2 max-w-3xl">As ferramentas abaixo são gratuitas e funcionam sem alterar a forma como as empresas publicam e pagam pelas vagas.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TOOLS.map(({ to, title, text, icon: Icon, className }) => (
              <Link key={to} to={to} className="bg-white border border-slate-200 rounded-2xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all group">
                <span className={`w-11 h-11 rounded-xl border grid place-items-center ${className}`}><Icon size={22} /></span>
                <h3 className="font-heading text-lg font-extrabold mt-4">{title}</h3>
                <p className="text-slate-600 mt-2 leading-relaxed text-sm">{text}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-blue-700 font-extrabold text-sm group-hover:gap-2 transition-all">Abrir <ArrowRight size={15} /></span>
              </Link>
            ))}
          </div>
        </section>

        <section id="vagas">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-950">Vagas disponíveis</h2>
              <p className="text-slate-600 mt-1">Novas oportunidades publicadas todos os dias.</p>
            </div>
            <button
              data-testid="clear-filters"
              onClick={limpar}
              className="px-4 py-2 rounded-xl bg-slate-950 text-white font-bold self-start hover:bg-slate-800 transition-colors"
            >
              Limpar filtros
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20 text-slate-500">
              <Loader2 className="animate-spin" size={32} />
            </div>
          ) : vagas.length === 0 ? (
            <div className="bg-white rounded-3xl p-10 border border-slate-200 text-center" data-testid="no-jobs">
              <strong className="text-lg font-heading">Nenhuma vaga encontrada.</strong>
              <p className="text-slate-600 mt-2">Tente buscar por outra cidade ou cargo.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="jobs-grid">
              {vagas.map((v, i) => (
                <JobCard key={v.id} vaga={v} index={i} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
